// @ts-nocheck
/* eslint react/prop-types: 0 */
import React, { useEffect, useRef, useState } from "react";
// @ts-ignore
import { useAllTranslationsData } from "@staticQueries";
import {
  parkPreviewSession,
  restorePreviewSession,
  isPreviewParked,
  PREVIEW_PARAM_KEYS,
} from "../utils/previewParams";
import dciLogo from "../images/DCI.png";

const font = "'Open Sans', system-ui, sans-serif";

// ── Icons ─────────────────────────────────────────────────────────────────────

const GlobeIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: "inline", verticalAlign: "middle", flexShrink: 0 }}>
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

const PdfIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: "inline", verticalAlign: "middle", flexShrink: 0 }}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="8" y1="13" x2="16" y2="13" />
    <line x1="8" y1="17" x2="13" y2="17" />
  </svg>
);

const DciLogoBox = () => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: '18px', height: '18px', background: '#0B83FF', borderRadius: '3px',
    flexShrink: 0, verticalAlign: 'middle',
  }}>
    <img src={dciLogo} alt="DCI" style={{ width: '12px', height: '12px', objectFit: 'contain' }} />
  </span>
);

const LinkIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

// ── Slug helper ───────────────────────────────────────────────────────────────

function slugify(text) {
  return (text || '')
    .replace(/<[^>]+>/g, '')       // strip HTML tags
    .replace(/&[a-z]+;/gi, '')     // strip HTML entities
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);
}

// ── Shared DOM helper — exported so preview template can call it too ───────────

/**
 * Stamps canonical IDs onto chapter/topic title elements.
 * Format: {slug(title)}-cid-{chapterId}  (chapters)
 *         {slug(title)}-cid-{chapterId}-tid-{topicId}  (topics)
 * Unique even when the same topic appears in multiple chapters.
 * Must be called AFTER ensureHeadingIds so it overrides slug-based IDs.
 */
export function addEntryIdAnchors(container, manual) {
  const chapterEls = [...container.querySelectorAll('.chapter')];
  (manual.chapters || []).forEach((chapter, i) => {
    const chapterEl = chapterEls[i];
    if (!chapterEl) return;
    const chapterHeading = chapterEl.querySelector('.title-chapter');
    if (chapterHeading) {
      chapterHeading.id = `${slugify(chapter.title || chapter.userTitle)}-cid-${chapter.id}`;
    }
    const topicEls = [...chapterEl.querySelectorAll('.topic')];
    (chapter.topics || []).forEach((topic, j) => {
      const topicEl = topicEls[j];
      if (!topicEl) return;
      const topicHeading = topicEl.querySelector('.title-topic');
      if (topicHeading) {
        topicHeading.id = `${slugify(topic.title || topic.userTitle)}-cid-${chapter.id}-tid-${topic.id}`;
      }
    });
  });
}

// ── Fallback element lookup by cid/tid suffix ─────────────────────────────────

/**
 * Find a heading element by its cid (and optionally tid) numeric IDs,
 * regardless of the title-slug prefix.  Used as fallback when the title has
 * changed and the exact ID no longer matches.
 */
function findHeadingByCidTid(hashOrId) {
  const id = hashOrId.startsWith('#') ? hashOrId.slice(1) : hashOrId;
  // New format: *-cid-{n} or *-cid-{n}-tid-{m}
  const cidMatch = id.match(/(?:^|-)cid-(\d+)(?:-tid-(\d+))?$/);
  // Old format: chapterId-{n} or chapterId-{n}-topicId-{m}
  const oldTopicMatch = !cidMatch && id.match(/^chapterId-(\d+)-topicId-(\d+)$/);
  const oldChapterMatch = !cidMatch && !oldTopicMatch && id.match(/^chapterId-(\d+)$/);

  let cid, tid;
  if (cidMatch) {
    [, cid, tid] = cidMatch;
  } else if (oldTopicMatch) {
    [, cid, tid] = oldTopicMatch;
  } else if (oldChapterMatch) {
    [, cid] = oldChapterMatch;
  } else {
    return null;
  }

  const suffix = tid ? `cid-${cid}-tid-${tid}` : `cid-${cid}`;
  return [...document.querySelectorAll('[id]')].find(e =>
    e.id.endsWith(`-${suffix}`) || e.id === suffix
  ) || null;
}

// ── URL parsing ───────────────────────────────────────────────────────────────

/**
 * Parse the current URL hash and build all link-card URLs.
 * Supports new format (#slug-cid-{n}-tid-{m}) and old format (#chapterId-{n}-topicId-{m}).
 */
function parseLinkCardFromUrl(manual, { isPreview = false } = {}) {
  const hash = window.location.hash || '';

  // New format: any-slug-cid-{n}-tid-{m} / any-slug-cid-{n}
  // Old format: #chapterId-{n}-topicId-{m} / #chapterId-{n}
  const topicMatch = hash.match(/-cid-(\d+)-tid-(\d+)$/) ||
                     hash.match(/^#chapterId-(\d+)-topicId-(\d+)$/);
  const chapterMatch = !topicMatch && (
    hash.match(/-cid-(\d+)$/) ||
    hash.match(/^#chapterId-(\d+)$/)
  );

  let appUrl, chapterId = null, topicId = null;

  if (topicMatch) {
    chapterId = topicMatch[1];
    topicId = topicMatch[2];
    appUrl = `dci-digiguide:///link/TopicArticle?id=${topicId}&chapterId=${chapterId}&manualId=${manual?.id}`;
  } else if (chapterMatch) {
    chapterId = chapterMatch[1];
    appUrl = `dci-digiguide:///link/Chapter?id=${chapterId}&manualId=${manual?.id}`;
  } else {
    appUrl = `dci-digiguide:///link/Manual?id=${manual?.id}`;
  }

  const chapter = chapterId ? manual?.chapters?.find(c => String(c.id) === chapterId) : null;
  const topic = topicId ? chapter?.topics?.find(t => String(t.id) === topicId) : null;

  const pdfBase = process.env.GATSBY_PDF_BASE_URL;
  const pdfUrl = pdfBase
    ? `${pdfBase}/DCI_DigiGuide_Manual_${manual?.id}_Site_${manual?.siteId}.pdf`
    : null;

  // Universal /link/ page URL — null when no manual ID available
  const linkUrl = manual?.id
    ? topicId
      ? `/link/TopicArticle?id=${topicId}&chapterId=${chapterId}&manualId=${manual.id}`
      : chapterId
        ? `/link/Chapter?id=${chapterId}&manualId=${manual.id}`
        : `/link/Manual?id=${manual.id}`
    : null;

  // Clean URL — strip preview params and 'link' param
  const cleanParams = new URLSearchParams(window.location.search);
  cleanParams.delete('link');
  PREVIEW_PARAM_KEYS.forEach(k => cleanParams.delete(k));
  const qs = cleanParams.toString();
  const currentUrl = window.location.pathname + (qs ? `?${qs}` : '') + hash;

  const staticWebUrl = manual?.id && manual?.siteId
    ? `/web/manuals/${manual.id}/site/${manual.siteId}${hash}`
    : currentUrl;

  return {
    appUrl,
    pdfUrl,
    linkUrl,
    webUrl: isPreview ? staticWebUrl : currentUrl,
    previewUrl: isPreview ? currentUrl : null,
    chapterId,
    topicId,
    chapterTitle: chapter?.title || null,
    topicTitle: topic?.title || null,
  };
}

// ── Build prod-view toggle URL ────────────────────────────────────────────────

function buildProdToggleUrl(enableProd) {
  const url = new URL(window.location.href);
  // Strip sensitive preview params — they live in sessionStorage, not links
  ['token', 'x-craft-live-preview', 'x-craft-preview', 'api-url'].forEach(k => url.searchParams.delete(k));
  if (enableProd) {
    url.searchParams.set('production', '1');
  } else {
    url.searchParams.delete('production');
  }
  return url.toString();
}

// ── Nav height helper ─────────────────────────────────────────────────────────

/** Read the always-visible nav bar height for scroll offset calculations. */
function getNavOffset() {
  return (parseInt(document.documentElement.style.getPropertyValue('--link-card-nav-height')) || 44) + 20;
}

/** Scroll to an element found by ID or cid/tid fallback, updating the hash. */
function scrollToHeading(hashOrId, opts = {}) {
  const id = (hashOrId.startsWith('#') ? hashOrId.slice(1) : hashOrId);
  let target = document.getElementById(id);
  if (!target) target = findHeadingByCidTid(id);
  if (!target) return;
  const offset = opts.offset !== undefined ? opts.offset : getNavOffset();
  const top = target.getBoundingClientRect().top + window.scrollY - offset;
  window.scrollTo({ top, behavior: opts.smooth ? 'smooth' : 'auto' });
  const newHash = `#${target.id}`;
  if (opts.smooth) {
    window.history.pushState(null, null, newHash);
  } else {
    window.history.replaceState(null, null, newHash);
  }
  window.dispatchEvent(new HashChangeEvent('hashchange', { oldURL: window.location.href, newURL: window.location.origin + window.location.pathname + newHash }));
}

// ── Link card sections hook ───────────────────────────────────────────────────
// Called unconditionally at top of component; guards internally when linkCard is null.

function useLinkCardSections({ manual, linkCard, isPreview, isProductionView }) {
  const allTranslations = useAllTranslationsData();
  const siteId = String(manual?.siteId || '1');
  const tMap = allTranslations[siteId] || {};
  const t = (key, fallback) => (tMap[key] !== undefined ? tMap[key] : fallback);

  if (!linkCard) return { LogoSection: null, BreadcrumbRow: null, ActionRow: null };

  const chapterLabel = t('screen_chapter', 'Chapter');
  const articleLabel = t('screen_article', 'Article');

  const { chapterId, topicId, chapterTitle, topicTitle, appUrl, pdfUrl, linkUrl, webUrl, previewUrl } = linkCard;
  const manualTitle = manual?.userTitle || manual?.title || '';

  const openContext = topicTitle
    ? { noun: t('article', 'Article'), title: topicTitle }
    : chapterTitle
      ? { noun: t('chapter', 'Chapter'), title: chapterTitle }
      : { noun: t('manual', 'Manual'), title: manualTitle };

  const handleAnchorClick = (e, href) => {
    if (!href || !href.startsWith('#')) return;
    e.preventDefault();
    scrollToHeading(href, { smooth: true });
  };

  const pageBase = typeof window !== 'undefined' ? window.location.pathname : '';
  const breadcrumbs = [
    { label: manualTitle, href: pageBase, dimmed: true },
    chapterTitle ? { label: `${chapterLabel}: ${chapterTitle}`, href: `#cid-${chapterId}` } : null,
    topicTitle ? { label: `${articleLabel}: ${topicTitle}`, href: `#cid-${chapterId}-tid-${topicId}` } : null,
  ].filter(Boolean);

  /* ── Logo section ───────────────────────────────────────────────────────── */
  const LogoSection = (
    <div
      style={{
        background: '#fff',
        borderBottom: '1px solid #eef0f5',
      }}
    >
      <div style={{
        maxWidth: '816px', margin: '0 auto',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '0.3rem 0.75rem', minHeight: '82px', gap: '0.1rem',
        fontFamily: font,
      }}>
        {manual?.productFamily && (
          <div style={{ fontSize: '0.65rem', fontWeight: 400, color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em', lineHeight: 1.2 }}>
            {manual.productFamily}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {manual?.logo?.url
            ? <img src={manual.logo.url} alt={manualTitle} style={{ maxWidth: '100%', maxHeight: '44px', objectFit: 'contain' }} />
            : <div style={{ width: '60px', height: '36px', background: '#e8edf5', borderRadius: '8px' }} />}
        </div>
        {manual?.variant && (
          <div style={{ fontSize: '0.65rem', fontWeight: 600, color: 'rgb(11,131,255)', textTransform: 'uppercase', letterSpacing: '0.06em', lineHeight: 1.2 }}>
            {manual.variant}
          </div>
        )}
      </div>
    </div>
  );

  /* ── Breadcrumb row ─────────────────────────────────────────────────────── */
  const BreadcrumbRow = (
    <div style={{ background: '#f0f4ff', borderBottom: '1px solid #dde6f5' }}>
      <div style={{
        maxWidth: '816px', margin: '0 auto',
        padding: '0.2rem 0.6rem',
        fontSize: '0.68rem', color: '#444', fontFamily: font,
        display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.15rem 0.3rem',
        minHeight: '24px',
      }}>
        {manual?.logo?.url && (
          <img
            src={manual.logo.url} alt=""
            style={{ maxHeight: '16px', width: 'auto', maxWidth: '48px', objectFit: 'contain', flexShrink: 0, opacity: 0.7 }}
          />
        )}
        {isPreview && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '3px',
            background: isProductionView ? '#dcfce7' : '#fef3c7',
            color: isProductionView ? '#166534' : '#92400e',
            fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.05em',
            padding: '1px 5px', borderRadius: '3px', flexShrink: 0,
          }}>
            <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'currentColor', display: 'inline-block' }} />
            {isProductionView ? 'PROD VIEW' : 'LIVE PREVIEW'}
          </span>
        )}
        {breadcrumbs.map((item, i) => (
          <React.Fragment key={i}>
            {(i > 0 || manual?.logo?.url || isPreview) && (
              <span style={{ color: '#bbb', flexShrink: 0, fontSize: '0.8rem', lineHeight: 1 }}>›</span>
            )}
            <a
              href={item.href}
              onClick={item.href?.startsWith('#') ? (e) => handleAnchorClick(e, item.href) : undefined}
              style={{
                color: item.dimmed ? '#aaa' : '#444',
                fontWeight: item.dimmed ? 400 : 500,
                wordBreak: 'break-word',
                textDecoration: 'none',
              }}
            >
              {item.label}
            </a>
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  /* ── Action row ─────────────────────────────────────────────────────────── */
  const isProdMode = isPreview
    ? new URLSearchParams(window.location.search).get('production') === '1'
    : false;

  const isParked = typeof window !== 'undefined' ? isPreviewParked() : false;

  const handleToggleLivePreview = () => {
    if (isParked) {
      restorePreviewSession();
      window.location.reload();
    } else {
      parkPreviewSession();
      const url = new URL(window.location.href);
      PREVIEW_PARAM_KEYS.forEach(k => url.searchParams.delete(k));
      window.location.href = url.toString();
    }
  };

  const ActionRow = (
    <div style={{ background: '#eef3fa', borderTop: '1px solid #dce5f7' }}>
      <div style={{
        maxWidth: '816px', margin: '0 auto',
        display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.3rem',
        padding: '0.25rem 0.4rem',
        fontFamily: font,
      }}>
        {/* ── Open in DigiGuide button ── */}
        <a href={appUrl} style={{
          flex: '1 1 auto',
          display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap',
          columnGap: '5px', rowGap: '2px',
          padding: '0.32rem 0.65rem',
          textDecoration: 'none',
          fontSize: '0.72rem', fontWeight: 700, color: '#fff',
          background: '#0066cc',
          borderRadius: '5px',
          cursor: 'pointer',
          minWidth: 0,
        }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
            <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
            <line x1="12" y1="18" x2="12.01" y2="18" />
          </svg>
          <span style={{ whiteSpace: 'nowrap' }}>Open {openContext.noun}:</span>
          <span style={{ wordBreak: 'break-word' }}>{openContext.title}</span>
          <span style={{ whiteSpace: 'nowrap' }}>in</span>
          <DciLogoBox />
          <span style={{ whiteSpace: 'nowrap', letterSpacing: '0.01em' }}>DigiGuide →</span>
        </a>

        {/* Force secondary buttons to their own row — consistent regardless of title length */}
        <div style={{ flexBasis: '100%', height: 0 }} />

        {/* ── PDF ── */}
        {pdfUrl && (
          <a href={pdfUrl} target="_blank" rel="noreferrer" style={{
            display: 'inline-flex', alignItems: 'center', gap: '3px',
            padding: '0.32rem 0.5rem',
            textDecoration: 'none',
            fontSize: '0.7rem', fontWeight: 600, color: '#444',
            background: '#dce5f7', borderRadius: '5px',
            flexShrink: 0, whiteSpace: 'nowrap', cursor: 'pointer',
          }}>
            <PdfIcon /><span>PDF ↗</span>
          </a>
        )}

        {/* ── Web (static rendered view) — only show in preview mode where it's a different URL ── */}
        {isPreview && (
          <a href={webUrl} style={{
            display: 'inline-flex', alignItems: 'center', gap: '3px',
            padding: '0.32rem 0.5rem',
            textDecoration: 'none',
            fontSize: '0.7rem', fontWeight: 600, color: '#0055aa',
            background: '#dce5f7', borderRadius: '5px',
            flexShrink: 0, whiteSpace: 'nowrap', cursor: 'pointer',
          }} title="Open static web view">
            <GlobeIcon /><span>Web ↗</span>
          </a>
        )}

        {/* ── Universal permalink ── */}
        {linkUrl && (
          <a href={linkUrl} style={{
            display: 'inline-flex', alignItems: 'center', gap: '3px',
            padding: '0.32rem 0.5rem',
            textDecoration: 'none',
            fontSize: '0.7rem', fontWeight: 600, color: '#333',
            background: '#dce5f7', borderRadius: '5px',
            flexShrink: 0, whiteSpace: 'nowrap', cursor: 'pointer',
          }} title="Universal permalink (app + web)">
            <LinkIcon /><span>Universal →</span>
          </a>
        )}

        {/* ── Home ── */}
        <a href="/" style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          padding: '0.32rem 0.45rem',
          textDecoration: 'none',
          background: '#dce5f7', borderRadius: '5px',
          flexShrink: 0, cursor: 'pointer',
        }} title="Home">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#444" strokeWidth="2" style={{ flexShrink: 0 }}>
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
        </a>

        {/* ── Preview-only controls ── */}
        {isPreview && (
          <>
            {/* Prod view toggle */}
            <a
              href={buildProdToggleUrl(!isProdMode)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '3px',
                padding: '0.32rem 0.5rem',
                textDecoration: 'none',
                fontSize: '0.7rem', fontWeight: 600,
                color: isProdMode ? '#92400e' : '#444',
                background: isProdMode ? '#fef3c7' : '#dce5f7',
                border: isProdMode ? '1px solid #f59e0b' : '1px solid transparent',
                borderRadius: '5px',
                flexShrink: 0, whiteSpace: 'nowrap', cursor: 'pointer',
              }}
              title={isProdMode ? 'Switch to live preview (show draft conditions)' : 'Switch to production view (resolve conditions)'}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
                <rect x="4" y="3" width="16" height="12" rx="1" />
                <line x1="4" y1="8" x2="20" y2="8" />
                <line x1="12" y1="3" x2="12" y2="8" />
                <line x1="1" y1="18" x2="23" y2="18" />
                <circle cx="5" cy="21" r="2" />
                <circle cx="12" cy="21" r="2" />
                <circle cx="19" cy="21" r="2" />
              </svg>
              <span>Prod</span>
            </a>

            {/* ImportExport */}
            <a href="/importexport" style={{
              display: 'inline-flex', alignItems: 'center', gap: '3px',
              padding: '0.32rem 0.5rem',
              textDecoration: 'none',
              fontSize: '0.7rem', fontWeight: 600, color: '#444',
              background: '#dce5f7', borderRadius: '5px',
              flexShrink: 0, whiteSpace: 'nowrap', cursor: 'pointer',
            }} title="Import / Export">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
                <polyline points="8 17 12 21 16 17" />
                <line x1="12" y1="12" x2="12" y2="21" />
                <polyline points="8 7 12 3 16 7" />
                <line x1="12" y1="3" x2="12" y2="12" />
              </svg>
              <span>I/E</span>
            </a>

            {/* (eye) Live Preview → link */}
            {previewUrl && (
              <a href={previewUrl} style={{
                display: 'inline-flex', alignItems: 'center', gap: '3px',
                padding: '0.32rem 0.5rem',
                textDecoration: 'none',
                fontSize: '0.7rem', fontWeight: 600, color: '#6600aa',
                background: '#ede5f7', borderRadius: '5px',
                flexShrink: 0, whiteSpace: 'nowrap', cursor: 'pointer',
              }} title="Open in web preview mode">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                <span>Live Preview →</span>
              </a>
            )}

            {/* Toggle Live Preview button */}
            <button
              onClick={handleToggleLivePreview}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '3px',
                padding: '0.32rem 0.5rem',
                border: 'none',
                fontSize: '0.7rem', fontWeight: 700,
                color: isParked ? '#166534' : '#fff',
                background: isParked ? '#dcfce7' : '#e05a00',
                borderRadius: '5px',
                flexShrink: 0, whiteSpace: 'nowrap', cursor: 'pointer',
              }}
              title={isParked ? 'Resume live preview' : 'Pause live preview'}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ flexShrink: 0 }}>
                <polyline points="1 4 1 10 7 10" />
                <polyline points="23 20 23 14 17 14" />
                <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" />
              </svg>
              <span>{isParked ? 'Resume Live Preview' : 'Toggle Live Preview'}</span>
            </button>
          </>
        )}
      </div>
    </div>
  );

  return { LogoSection, BreadcrumbRow, ActionRow };
}

// ── WebViewHeader component ───────────────────────────────────────────────────

/**
 * Fixed top header for web view and preview pages.
 *
 * The logo section scrolls away with the page (top = -min(scrollY, logoHeight));
 * the nav row (breadcrumb + action) stays pinned at the top once the logo is gone.
 * No CSS animation — the collapse is driven entirely by scroll position.
 *
 * Props:
 *   manual            — resolved manual object
 *   containerRef      — ref to the scrollable content container (for active-section tracking)
 *   isPreview         — show preview badge + importexport + exit-preview button
 *   isProductionView  — affects badge label (PROD VIEW vs LIVE PREVIEW)
 */
const WebViewHeader = ({
  manual,
  containerRef,
  isPreview = false,
  isProductionView = false,
}) => {
  const [linkCard, setLinkCard] = useState(null);
  const linkCardRef = useRef(null);
  const logoSectionRef = useRef(null);
  const navSectionRef = useRef(null);

  // useLinkCardSections must be called unconditionally (contains hooks)
  const { LogoSection, BreadcrumbRow, ActionRow } = useLinkCardSections({
    manual,
    linkCard,
    isPreview,
    isProductionView,
  });

  // Initialise link card from URL and update on hash changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const update = () => setLinkCard(parseLinkCardFromUrl(manual, { isPreview }));
    update();
    window.addEventListener('hashchange', update);
    return () => window.removeEventListener('hashchange', update);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll: slide logo away with scroll, active-section tracking
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Set initial top without putting it in React inline style (avoids re-render reset)
    if (linkCardRef.current) linkCardRef.current.style.top = '0px';

    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          // Scroll the full header up; logo disappears, nav bar pins at top
          if (linkCardRef.current && logoSectionRef.current) {
            const logoH = logoSectionRef.current.offsetHeight;
            const scrolledLogo = Math.min(window.scrollY, logoH);
            linkCardRef.current.style.top = `-${scrolledLogo}px`;
            // Track visible bottom so hamburger flyout anchors to it
            const visibleBottom = linkCardRef.current.offsetHeight - scrolledLogo;
            document.documentElement.style.setProperty('--link-card-bottom', `${visibleBottom}px`);
          }

          // Active-section tracking — update breadcrumb as user scrolls
          if (containerRef && containerRef.current) {
            const navH = parseInt(document.documentElement.style.getPropertyValue('--link-card-nav-height')) || 44;
            const headerOffset = navH + 20;
            const allHeadings = [...containerRef.current.querySelectorAll('.title-chapter[id], .title-topic[id]')];
            let activeHeading = null;
            for (const h of allHeadings) {
              if (h.getBoundingClientRect().top <= headerOffset) {
                activeHeading = h;
              } else {
                break;
              }
            }
            if (activeHeading) {
              const newHash = `#${activeHeading.id}`;
              if (newHash !== window.location.hash) {
                window.history.replaceState(null, null, newHash);
                setLinkCard(parseLinkCardFromUrl(manual, { isPreview }));
              }
            } else if (window.scrollY < 50 && window.location.hash) {
              // Scrolled back to top — clear hash so breadcrumb shows just the manual
              window.history.replaceState(null, null, window.location.pathname + window.location.search);
              setLinkCard(parseLinkCardFromUrl(manual, { isPreview }));
            }
          }

          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Smooth scroll for all in-content anchor links
  useEffect(() => {
    const container = containerRef && containerRef.current;
    if (!container) return;
    const handleClick = (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const href = a.getAttribute('href');
      e.preventDefault();
      scrollToHeading(href, { smooth: true });
    };
    container.addEventListener('click', handleClick);
    return () => container.removeEventListener('click', handleClick);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Measure header + nav heights — update CSS vars (drives padding-top via CSS, no React re-render)
  useEffect(() => {
    if (!linkCard || !linkCardRef.current) {
      document.documentElement.style.removeProperty('--link-card-height');
      document.documentElement.style.removeProperty('--link-card-nav-height');
      return;
    }
    const totalEl = linkCardRef.current;
    const navEl = navSectionRef.current;
    const update = () => {
      const totalH = totalEl.offsetHeight;
      document.documentElement.style.setProperty('--link-card-height', `${totalH}px`);
      if (navEl) {
        document.documentElement.style.setProperty('--link-card-nav-height', `${navEl.offsetHeight}px`);
      }
      // --link-card-bottom tracks the visible bottom of the header at current scroll
      const logoH = logoSectionRef.current ? logoSectionRef.current.offsetHeight : 0;
      const scrolledLogo = Math.min(window.scrollY || 0, logoH);
      document.documentElement.style.setProperty('--link-card-bottom', `${totalH - scrolledLogo}px`);
    };
    update();
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(update);
      ro.observe(totalEl);
      if (navEl) ro.observe(navEl);
      return () => ro.disconnect();
    }
  }, [linkCard]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleHamburger = (e) => {
    if (e) e.nativeEvent?.stopImmediatePropagation();
    const btn = document.getElementById('integrated-hamburger-menu')?.querySelector('.hamburger-toggle-btn');
    if (btn) btn.click();
  };

  if (!linkCard) return null;

  return (
    <div
      ref={linkCardRef}
      style={{
        position: 'fixed',
        // top is set via DOM in scroll handler, not React inline style
        left: 0, right: 0, zIndex: 500,
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 2px 12px rgba(0,0,0,0.10)',
        pointerEvents: 'auto',
      }}
    >
      {/* Logo row — full width, white background, scrolls away with page */}
      <div ref={logoSectionRef}>
        {LogoSection}
      </div>
      {/* Nav row — hamburger | breadcrumb+action | right spacer */}
      <div ref={navSectionRef} style={{ display: 'flex', alignItems: 'stretch' }}>
        <button
          className="link-card-hamburger-btn"
          onClick={toggleHamburger}
          aria-label="Toggle Table of Contents"
          title="Table of Contents"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          {BreadcrumbRow}
          {ActionRow}
        </div>
        <div className="link-card-right-spacer" aria-hidden="true" />
      </div>
    </div>
  );
};

export default WebViewHeader;
